import { formatMoney } from '@theme/money-formatting';

const STORAGE_KEY = 'og-favorite-products';

/** @returns {{ id: string, handle: string }[]} */
function getFavorites() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(stored) ? stored : [];
  } catch (error) {
    return [];
  }
}

/** @param {{ id: string, handle: string }[]} favorites */
function setFavorites(favorites) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  } catch (error) {
    // localStorage unavailable (e.g. private browsing) - favorites just won't persist
  }
}

function removeFavorite(id) {
  setFavorites(getFavorites().filter((item) => item.id !== id));

  document.dispatchEvent(
    new CustomEvent('product-favorite:change', {
      detail: { productId: id, isFavorited: false },
    })
  );
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

/**
 * Renders the contents of the wishlist drawer. Fetches each favorited
 * product's data (via Shopify's `.js` product endpoint) when the drawer opens.
 */
class WishlistDrawerContent extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.wishlist-drawer__list');
    this.emptyState = this.querySelector('.wishlist-drawer__empty');
    this.moneyFormat = this.querySelector('.wishlist-drawer__money-format')?.content.textContent || '{{amount}}';
    this.currency = this.dataset.currency || '';

    document.addEventListener('theme-drawer:open', this.#handleDrawerOpen);
    document.addEventListener('product-favorite:change', this.#handleFavoriteChange);
  }

  disconnectedCallback() {
    document.removeEventListener('theme-drawer:open', this.#handleDrawerOpen);
    document.removeEventListener('product-favorite:change', this.#handleFavoriteChange);
  }

  #handleDrawerOpen = (event) => {
    if (event.target?.id !== 'wishlist-drawer') return;
    this.#render();
  };

  #handleFavoriteChange = () => {
    if (this.closest('theme-drawer')?.hasAttribute('open')) {
      this.#render();
    }
  };

  async #render() {
    const favorites = getFavorites();

    if (favorites.length === 0) {
      this.list.innerHTML = '';
      this.emptyState.hidden = false;
      return;
    }

    this.emptyState.hidden = true;

    const products = await Promise.all(
      favorites.map(async (item) => {
        try {
          const response = await fetch(`/products/${item.handle}.js`);
          if (!response.ok) return null;
          return await response.json();
        } catch (error) {
          return null;
        }
      })
    );

    this.list.innerHTML = favorites
      .map((favorite, index) => {
        const product = products[index];
        if (!product) return '';

        const image = product.featured_image || (product.images && product.images[0]) || '';
        const price = formatMoney(product.price, this.moneyFormat, this.currency);

        return `
          <li class="wishlist-drawer__item">
            <a class="wishlist-drawer__item-link" href="${escapeHtml(product.url)}">
              ${
                image
                  ? `<img class="wishlist-drawer__item-image" src="${escapeHtml(image)}&width=160" width="64" height="64" alt="" loading="lazy">`
                  : ''
              }
              <span class="wishlist-drawer__item-details">
                <span class="wishlist-drawer__item-title">${escapeHtml(product.title)}</span>
                <span class="wishlist-drawer__item-price">${price}</span>
              </span>
            </a>
            <button
              type="button"
              class="wishlist-drawer__item-remove"
              data-product-id="${escapeHtml(favorite.id)}"
              aria-label="Remove ${escapeHtml(product.title)} from wishlist"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </li>
        `;
      })
      .join('');

    this.list.querySelectorAll('.wishlist-drawer__item-remove').forEach((button) => {
      button.addEventListener('click', () => {
        removeFavorite(button.dataset.productId);
        this.#render();
      });
    });
  }
}

if (!customElements.get('wishlist-drawer-content')) {
  customElements.define('wishlist-drawer-content', WishlistDrawerContent);
}

/**
 * The shared <theme-drawer> only closes on outside clicks when a native
 * ::backdrop is present (narrow/modal viewports). On wide viewports it
 * squeezes page content instead and has no backdrop to click, so this
 * mirrors the header drawer's always-on click-outside-to-close behavior.
 */
document.addEventListener('click', (event) => {
  const drawer = document.getElementById('wishlist-drawer');
  if (!drawer || !drawer.hasAttribute('open')) return;
  if (drawer.contains(event.target)) return;
  if (event.target.closest('[aria-controls="wishlist-drawer"]')) return;

  drawer.close();
});
